import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { confirm } from 'devextreme/ui/dialog';
import { from, map, Observable, of, repeat, retry, switchMap, takeWhile, tap, timer } from 'rxjs';
import { NebulaInfoService } from './nebula-info.service';
import { WINDOW } from '@tokens/window.token';

/** Wartezeit bis zur erneuten Prüfung nach Ablehnung oder Fehler (30 Minuten) */
const RECHECK_DELAY_MS = 1000 * 60 * 30;

/**
 * Prüft beim Start, ob der Server eine andere Web-App-Version meldet als die des Clients,
 * und fordert dann zum Neuladen auf.
 */
@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private currentClientVersion = inject<string>('NEBULA_CLIENT_VERSION' as never);
  private nebulaInfoService = inject(NebulaInfoService);
  private _window = inject<Window>(WINDOW);
  private destroyRef = inject(DestroyRef);

  /**
   * Startet die Versionsprüfung. Im Entwicklungs-Build (Platzhalter als Version) findet keine Prüfung statt.
   * Gleiche Version oder bestätigtes Neuladen beenden die Prüfung; Ablehnung oder Fehler der Abfrage
   * führen nach 30 Minuten zu einem erneuten Durchlauf.
   */
  start(): void {
    if (this.currentClientVersion === '4.2.0-alpha.6205') {
      return;
    }

    // Ein Durchlauf liefert genau einen Wert: true, wenn erneut geprüft werden soll (Ablehnung)
    const check$ = this.nebulaInfoService.webAppVersion().pipe(
      // Abfrage fehlgeschlagen: nach 30 Minuten erneut abfragen
      retry({ delay: () => timer(RECHECK_DELAY_MS) }),
      switchMap(info =>
        this.hasVersionChanged(info.version, this.currentClientVersion) ? this.confirmReload(info.version) : of(false)
      )
    );

    check$
      .pipe(
        // nach Ablehnung 30 Minuten später erneut prüfen; sonst endet die Kette, bevor wiederholt wird
        repeat({ delay: RECHECK_DELAY_MS }),
        takeWhile(recheck => recheck),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /** Fragt den Benutzer nach dem Neuladen; liefert true bei Ablehnung */
  private confirmReload(version: string): Observable<boolean> {
    const title = 'AgiliCision-Update';
    const message = `Der Anwendungserver wurde auf eine neue Programmversion (${version}) aktualisiert. <br> Um Probleme im weiteren Betrieb zu vermeiden, laden Sie die Web-Seite bitte neu.<br> <br> Möchten Sie die Web-Seite jetzt neu laden?`;
    return from(confirm(message, title)).pipe(
      tap(confirmed => {
        if (confirmed) {
          // erst den lokalen Speicher leeren, dann neu laden
          localStorage.clear();
          this._window.location.reload();
        }
      }),
      map(confirmed => !confirmed)
    );
  }

  private hasVersionChanged(currentVersion: string, lastVersion: string): boolean {
    return !!lastVersion && currentVersion !== lastVersion;
  }
}
